import React, { useState, useEffect } from "react";
import { Space } from 'react-zoomable-ui';
import Waypoint from './Waypoint';
import Orbit from './Orbit';
import Star from './Star';
import styled from "styled-components";
import { getBounds } from "../../utils/calculations";

const Grid = styled.div`
  --grid-color: var(--color-theme-primary-400);  
  background-image: linear-gradient(to right, var(--grid-color) 1px, transparent 1px), linear-gradient(to bottom, var(--grid-color) 1px, transparent 1px);
  background-position: center center;
  background-size: var(--level) var(--level);
  inset: 0;
  pointer-events: none;
  position: absolute;
  transition-origin: center center;
  z-index: -1;
`;

const ZoomIndicator = styled.div`
  background-color: var(--window-background);
  border: 1px solid var(--window-border);
  bottom: var(--space-6);
  display: flex;
  flex-direction: column;
  height: 33.33%;
  justify-content: space-around;
  position: absolute;
  right: var(--zoom-indicator-right, calc(var(--space-6) * 2));
  transition-property: right;
  transition-duration: 250ms;
  transition-timing-function: ease-in;
  width: var(--space-1);
  border-radius: 999px;

  span {
    margin-inline-start: 1rem;
    position: relative;

    &::before {
      position: absolute;
      top: 50%;
      translate: 0 -50%;
      left: calc(var(--space-2) * -2);
      content: '';
      width: calc(var(--space-2) * 1.5);
      height: 1px;
      background-color: var(--window-border);
    }
  }

  &::after {
    background-color: var(--color-theme-secondary-400);
    content: '';
    height: 0.25rem;
    border-radius: 999px;
    left: -0.5rem;
    position: absolute;
    top: var(--level);
    width: calc(100% + 1rem);
  }
`;

function System({ store }) {
  const [zoom, setZoom] = useState(1.0);
  const bounds = getBounds(store.state.system.waypoints);
  const selectedShip = store.state.selectedShip;
  const [view, setView] = useState(null);


  // Count moons and stuff at this same location
  let places = {};
  function getIndexAtPlace(waypoint) {
    const i = waypoint.x + '|' +  waypoint.y;
    if (places[i] === undefined) {
      places[i] = 1;
    } else {
      places[i] += 1;
    }

    return places[i];
  }

  function zoomLevelToPercent(zoom) {
    const minZoom = 0;
    const maxZoom = 2.5;
    const percentage = ((zoom - minZoom) / (maxZoom - minZoom)) * 100;
    return Math.round(Math.max(0, Math.min(100, percentage)));
  }

  function zoomLevelToGridSize(zoom) {
    const minZoom = 0;
    const maxZoom = 2.5;
    const minPx = 4;
    const maxPx = 96;
    const gridSize = minPx + ((zoom - minZoom) / (maxZoom - minZoom)) * (maxPx - minPx);
    
    return Math.round(Math.max(minPx, Math.min(maxPx, gridSize)));
  }

  useEffect(() => {
    if (selectedShip) {
      const ship = store.state.ships.find(s => s.symbol == selectedShip);
      if (ship.nav.waypointSymbol) {
        const waypoint = store.state.system.waypoints.find(w => w.symbol == ship.nav.waypointSymbol);
        if (view && waypoint) {
          view.camera.centerFitAreaIntoView({
            left: (waypoint.x * 10) - 500,
            top: (waypoint.y * 10) - 500,
            width: 1000,
            height: 1000,
          });
        }
      }
    }
  }, [selectedShip]);

  return (
    <>
      <Grid style={{ '--level': zoomLevelToGridSize(zoom) + 'px' }} />
      <ZoomIndicator style={{ '--level': zoomLevelToPercent(zoom) + '%' }}>
        <span>0</span>
        <span>1</span>
        <span>2</span>
        <span>3</span>
        <span>4</span>
        <span>5</span>
        <span>6</span>
        <span>7</span>
        <span>8</span>
        <span>9</span>
        <span>10</span>
      </ZoomIndicator>
      <Space
          onCreate={(viewPort) => {
            viewPort.setBounds({/* x: [0, bounds.x], y: [0, bounds.y],*/ zoom: [0.1, 2.5] });
            viewPort.camera.centerFitAreaIntoView({
              left: 0,
              top: 0,
              width: bounds.minx + bounds.maxx,
              height: bounds.miny + bounds.maxy,
            })
            setView(viewPort);
          }}
          onUpdated={(viewPort) => {
            setZoom(viewPort.zoomFactor);
          }}>
        <>
          <Star store={store} zoom={zoom} />
          {store.state.system.waypoints.map((waypoint, idx) => (
            <React.Fragment key={idx}>
              <Waypoint store={ store } waypoint={ waypoint } zoom={ zoom } place={ getIndexAtPlace(waypoint) } />
              <Orbit store={ store } waypoint={ waypoint } zoom={ zoom } />
            </React.Fragment>
          ))}
        </>
      </Space>
    </>
  );
}

export default System;
